import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Loader2, X, Link as LinkIcon, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Add-a-job modal. Two tabs:
 *   url - paste a posting URL; backend scrapes + LLM-parses.
 *   manual - paste the listing text manually.
 * On success, calls onCreated(jobId) so the parent can refresh + navigate.
 */
export default function AddCustomJobModal({ open, onClose, onCreated }) {
  const [tab, setTab] = useState("url");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // URL tab state
  const [url, setUrl] = useState("");

  // Manual tab state
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [manualUrl, setManualUrl] = useState("");

  useEffect(() => {
    if (!open) {
      setTab("url");
      setSubmitting(false);
      setError(null);
      setUrl("");
      setTitle("");
      setCompany("");
      setLocation("");
      setDescription("");
      setManualUrl("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !submitting) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const payload =
        tab === "url"
          ? { mode: "url", url: url.trim() }
          : {
              mode: "manual",
              title: title.trim(),
              company: company.trim(),
              location: location.trim(),
              description: description.trim(),
              url: manualUrl.trim(),
            };
      const res = await api.post("/jobs/custom", payload);
      toast.success(`Added "${res.data.title}". Tailoring materials in the background.`);
      onCreated?.(res.data.job_id);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't add that job. Try the other tab.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const urlValid = url.trim().length > 6;
  const manualValid = title.trim().length > 1 && description.trim().length >= 80;
  const canSubmit = !submitting && (tab === "url" ? urlValid : manualValid);

  return (
    <div
      data-testid="add-job-modal"
      className="fixed inset-0 z-[120] flex items-start justify-center pt-12 md:pt-20 px-4 bg-background/85 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget && !submitting) onClose(); }}
    >
      <div className="panel-glass corner-marks relative w-full max-w-2xl p-6 md:p-8">
        <button
          data-testid="add-job-close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close"
          className="absolute top-3 right-3 p-2 text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="terminal-label text-primary mb-2">[ ADD.A.JOB ]</div>
        <h2 className="font-display text-2xl uppercase font-bold tracking-tight mb-2">
          Add a listing we missed
        </h2>
        <p className="text-xs text-muted-foreground mb-6 leading-relaxed max-w-lg">
          Drop a URL and we&apos;ll scrape it for you - works on most ATS pages (Greenhouse, Lever, Ashby, Workable, careers pages). If the URL won&apos;t parse, paste the listing text in the second tab.
        </p>

        <div className="flex border border-foreground/20 mb-6">
          <TabButton
            active={tab === "url"}
            onClick={() => { setTab("url"); setError(null); }}
            icon={LinkIcon}
            label="From URL"
            testId="add-job-tab-url"
          />
          <TabButton
            active={tab === "manual"}
            onClick={() => { setTab("manual"); setError(null); }}
            icon={FileText}
            label="Paste manually"
            testId="add-job-tab-manual"
          />
        </div>

        {tab === "url" ? (
          <div className="space-y-3" data-testid="add-job-url-form">
            <label className="terminal-label block">Job posting URL</label>
            <input
              data-testid="add-job-url-input"
              autoFocus
              type="url"
              placeholder="https://jobs.lever.co/company/role-id"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={submitting}
              className="input-base w-full"
            />
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-mono">
              Note · LinkedIn pages often don&apos;t scrape. If parsing fails, use the manual tab.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="add-job-manual-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="terminal-label block mb-1">Title *</label>
                <input
                  data-testid="add-job-manual-title"
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={submitting}
                  placeholder="Senior Brand Designer"
                  className="input-base w-full"
                />
              </div>
              <div>
                <label className="terminal-label block mb-1">Company</label>
                <input
                  data-testid="add-job-manual-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  disabled={submitting}
                  placeholder="Specimen Studio"
                  className="input-base w-full"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="terminal-label block mb-1">Location</label>
                <input
                  data-testid="add-job-manual-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  disabled={submitting}
                  placeholder="Remote (US)"
                  className="input-base w-full"
                />
              </div>
              <div>
                <label className="terminal-label block mb-1">Posting URL <span className="text-muted-foreground/70 normal-case tracking-normal text-[10px]">(optional)</span></label>
                <input
                  data-testid="add-job-manual-url"
                  value={manualUrl}
                  onChange={(e) => setManualUrl(e.target.value)}
                  disabled={submitting}
                  placeholder="https://…"
                  className="input-base w-full"
                />
              </div>
            </div>
            <div>
              <label className="terminal-label block mb-1">Full job description *</label>
              <textarea
                data-testid="add-job-manual-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={submitting}
                placeholder="Paste the entire listing - responsibilities, requirements, nice-to-haves. The more context, the better the tailored materials."
                rows={9}
                className="input-base w-full resize-y font-mono text-xs leading-relaxed"
              />
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/70 font-mono mt-1">
                {description.length} chars · min 80
              </div>
            </div>
          </div>
        )}

        {error && (
          <div data-testid="add-job-error" className="mt-4 p-3 border border-destructive/50 bg-destructive/5 text-xs text-destructive font-mono">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 mt-6 pt-5 border-t border-border">
          <button
            onClick={onClose}
            disabled={submitting}
            className="btn-ghost text-[11px]"
            data-testid="add-job-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="add-job-submit"
          >
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            {submitting
              ? (tab === "url" ? "Scraping…" : "Adding…")
              : (tab === "url" ? "Scrape & add →" : "Add to my feed →")}
          </button>
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex-1 px-4 py-3 text-[11px] uppercase tracking-[0.2em] font-mono flex items-center justify-center gap-2 transition-colors ${
        active
          ? "bg-foreground text-background"
          : "bg-background text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
      {label}
    </button>
  );
}
